/**
 * NIS2-Betroffenheitscheck – die Einstufungslogik, getrennt von der Oberfläche.
 *
 * Grundlage ist das NISG 2026 (in Kraft ab 1. Oktober 2026) mit den Sektoren
 * seiner Anlagen 1 und 2 und den Größenschwellen der EU-KMU-Definition.
 * Stand der Prüfung: September 2026, Quellen: WKO-Übersicht zum NISG 2026,
 * Schönherr „NISG 2026 – Alles, was Sie wissen müssen“.
 *
 * Bewusst als unverbindliche Ersteinschätzung angelegt. Die Einzelfallprüfung
 * – Haupttätigkeit nach ÖNACE, Konzernzurechnung, Sonderfälle – kann ein
 * Formular nicht leisten. Das Ergebnis soll eine Richtung geben und das
 * Gespräch vorbereiten, nicht es ersetzen. Genau so steht es auch auf der
 * Ergebnisseite.
 *
 * Alles läuft im Browser. Keine Angabe verlässt das Gerät.
 */

export type Groesse = "klein" | "mittel" | "gross";
export type Sektorgruppe = "anlage1" | "anlage2" | "groessenunabhaengig" | "keiner";

export type Sektor = { value: string; label: string; gruppe: Sektorgruppe };

/**
 * Die Sektoren in der Sprache der Zielgruppe statt im Gesetzeswortlaut.
 * Die Teilbereiche des verarbeitenden Gewerbes sind einzeln aufgeführt, weil
 * gerade dort die meisten Fehleinschätzungen passieren: Maschinenbau und
 * Fahrzeugbau fallen unter Anlage 2, allgemeine Metallverarbeitung nicht.
 */
export const sektoren: Sektor[] = [
  // Anlage 1 – Sektoren mit hoher Kritikalität
  { value: "energie", label: "Energie (Strom, Gas, Fernwärme, Öl, Wasserstoff)", gruppe: "anlage1" },
  { value: "verkehr", label: "Verkehr (Luft, Schiene, Schifffahrt, Straße)", gruppe: "anlage1" },
  { value: "bank", label: "Bankwesen", gruppe: "anlage1" },
  { value: "finanzmarkt", label: "Finanzmarktinfrastrukturen", gruppe: "anlage1" },
  { value: "gesundheit", label: "Gesundheitswesen", gruppe: "anlage1" },
  { value: "trinkwasser", label: "Trinkwasserversorgung", gruppe: "anlage1" },
  { value: "abwasser", label: "Abwasser", gruppe: "anlage1" },
  { value: "digitale-infrastruktur", label: "Rechenzentren, Cloud, Internetknoten, CDN", gruppe: "anlage1" },
  { value: "ikt-b2b", label: "IT-Dienste für andere Unternehmen (Managed Services, Managed Security)", gruppe: "anlage1" },
  { value: "weltraum", label: "Weltraum", gruppe: "anlage1" },

  // Anlage 2 – sonstige kritische Sektoren
  { value: "maschinenbau", label: "Maschinenbau", gruppe: "anlage2" },
  { value: "fahrzeugbau", label: "Fahrzeugbau und Kfz-Teile", gruppe: "anlage2" },
  { value: "elektro", label: "Elektronik, elektrische Ausrüstung, Datenverarbeitungsgeräte", gruppe: "anlage2" },
  { value: "medizinprodukte", label: "Medizinprodukte und In-vitro-Diagnostika", gruppe: "anlage2" },
  { value: "chemie", label: "Chemie (Herstellung, Einfuhr, Vertrieb)", gruppe: "anlage2" },
  { value: "lebensmittel", label: "Lebensmittel (industrielle Produktion, Verarbeitung, Großhandel)", gruppe: "anlage2" },
  { value: "post", label: "Post- und Kurierdienste", gruppe: "anlage2" },
  { value: "abfall", label: "Abfallbewirtschaftung", gruppe: "anlage2" },
  { value: "digitale-dienste", label: "Online-Marktplätze, Suchmaschinen, soziale Netzwerke", gruppe: "anlage2" },
  { value: "forschung", label: "Forschungseinrichtungen", gruppe: "anlage2" },

  // Größenunabhängig erfasst
  { value: "telekom", label: "Öffentliche Telekommunikationsnetze oder -dienste", gruppe: "groessenunabhaengig" },
  { value: "dns-tld", label: "DNS-Dienste oder Domain-Registrierung", gruppe: "groessenunabhaengig" },
  { value: "vertrauensdienste", label: "Qualifizierte Vertrauensdienste (z. B. elektronische Signaturen)", gruppe: "groessenunabhaengig" },
  { value: "verwaltung", label: "Öffentliche Verwaltung", gruppe: "groessenunabhaengig" },

  // Nicht in den Anlagen
  { value: "zulieferteile", label: "Metallverarbeitung, Kunststoff, Zulieferteile", gruppe: "keiner" },
  { value: "andere", label: "Anderer Bereich (Handel, Handwerk, Dienstleistung, Bau …)", gruppe: "keiner" },
];

export const sektorGruppenTitel: Record<Sektorgruppe, string> = {
  anlage1: "Sektoren mit hoher Kritikalität",
  anlage2: "Sonstige kritische Sektoren",
  groessenunabhaengig: "Unabhängig von der Größe erfasst",
  keiner: "Andere Bereiche",
};

export type Antworten = {
  sektor: string;
  mitarbeiter: "unter-50" | "50-249" | "ab-250";
  finanzen: "bis-10" | "10-50" | "ueber-50";
  konzern: "ja" | "nein";
  lieferant: "ja" | "nein" | "unklar";
  anforderung: "ja" | "nein";
};

type Option<T extends string> = { value: T; label: string };

/** Die Fragen 2–6 in der Reihenfolge des Playbooks; Frage 1 ist die Sektorwahl. */
export const fragen: {
  id: Exclude<keyof Antworten, "sektor">;
  frage: string;
  hilfe: string;
  optionen: Option<string>[];
}[] = [
  {
    id: "mitarbeiter",
    frage: "Wie viele Mitarbeitende hat Ihr Unternehmen?",
    hilfe: "Gezählt werden Vollzeitäquivalente. Teilzeitkräfte anteilig.",
    optionen: [
      { value: "unter-50", label: "unter 50" },
      { value: "50-249", label: "50 bis 249" },
      { value: "ab-250", label: "250 oder mehr" },
    ],
  },
  {
    id: "finanzen",
    frage: "Wie hoch sind Jahresumsatz und Bilanzsumme?",
    hilfe: "Für die Schwelle müssen Umsatz UND Bilanzsumme darüber liegen.",
    optionen: [
      { value: "bis-10", label: "Höchstens 10 Mio. € (Umsatz oder Bilanzsumme)" },
      { value: "10-50", label: "Beide über 10 Mio. €, aber nicht über 50 / 43 Mio. €" },
      { value: "ueber-50", label: "Umsatz über 50 Mio. € und Bilanzsumme über 43 Mio. €" },
    ],
  },
  {
    id: "konzern",
    frage: "Gehört Ihr Unternehmen zu einer Unternehmensgruppe?",
    hilfe: "Gemeint sind Beteiligungen von über 25 % – in beide Richtungen. Deren Zahlen zählen bei der Größe mit, außer Ihre IT ist von der Gruppe unabhängig.",
    optionen: [
      { value: "nein", label: "Nein, eigenständig" },
      { value: "ja", label: "Ja, Teil einer Gruppe" },
    ],
  },
  {
    id: "lieferant",
    frage: "Beliefern oder betreuen Sie Unternehmen aus Energie, Gesundheit, Verkehr, Industrie, IT oder öffentlicher Verwaltung?",
    hilfe: "Diese Kunden müssen die Sicherheit ihrer Lieferkette nachweisen – und geben das an ihre Lieferanten weiter.",
    optionen: [
      { value: "ja", label: "Ja" },
      { value: "nein", label: "Nein" },
      { value: "unklar", label: "Weiß ich nicht" },
    ],
  },
  {
    id: "anforderung",
    frage: "Hat ein Kunde bereits Sicherheitsnachweise oder einen NIS2-Fragebogen angefordert?",
    hilfe: "Etwa ein Fragebogen, ein Vertragsnachtrag oder eine Selbstauskunft zur Informationssicherheit.",
    optionen: [
      { value: "ja", label: "Ja" },
      { value: "nein", label: "Nein, noch nicht" },
    ],
  },
];

const RANG: Record<Groesse, number> = { klein: 0, mittel: 1, gross: 2 };

/**
 * Größe nach EU-Definition: mittel ab 50 Beschäftigten ODER wenn Umsatz UND
 * Bilanzsumme 10 Mio. € übersteigen; groß ab 250 Beschäftigten ODER wenn der
 * Umsatz 50 Mio. € und die Bilanzsumme 43 Mio. € übersteigen. Maßgeblich ist
 * das höhere der beiden Kriterien.
 */
export function groesse(a: Pick<Antworten, "mitarbeiter" | "finanzen">): Groesse {
  const nachKoepfen: Groesse = a.mitarbeiter === "ab-250" ? "gross" : a.mitarbeiter === "50-249" ? "mittel" : "klein";
  const nachZahlen: Groesse = a.finanzen === "ueber-50" ? "gross" : a.finanzen === "10-50" ? "mittel" : "klein";
  return RANG[nachKoepfen] >= RANG[nachZahlen] ? nachKoepfen : nachZahlen;
}

export type ErgebnisKey = "wesentlich" | "wichtig" | "groessenunabhaengig" | "grenzfall" | "indirekt" | "nicht";

export type Empfehlung = { titel: string; text: string; href: string; cta: string };

export type Ergebnis = {
  key: ErgebnisKey;
  titel: string;
  kurz: string;
  gruende: string[];
  /** Nur bei direkter Betroffenheit: was ab jetzt gilt. */
  pflichten: string[];
  empfehlung: Empfehlung;
  zweitEmpfehlung?: Empfehlung;
  /** Kurztext, mit dem das Kontaktformular vorbelegt wird. */
  anlass: string;
};

/** Pflichten direkt betroffener Einrichtungen – nur belegte Fristen. */
const PFLICHTEN_DIREKT = [
  "Registrierung bei der Behörde bis 31. Dezember 2026",
  "Risikomanagementmaßnahmen nach § 32 NISG 2026 umsetzen",
  "Die Geschäftsführung stellt die Einhaltung der Maßnahmen sicher, beaufsichtigt sie und nimmt an Cybersicherheitsschulungen teil (§ 31)",
  "Den Mitarbeitenden regelmäßig Schulungen anbieten (§ 31)",
  "Erhebliche Sicherheitsvorfälle melden – Frühwarnung binnen 24 Stunden",
  "Selbstdeklaration bis 30. September 2027",
];

const GAP: Empfehlung = {
  titel: "Gap-Analyse & Maßnahmenplan",
  text: "Wo Sie bei den zehn Maßnahmenbereichen stehen, was zuerst kommt und bis wann – mit Fristenkalender.",
  href: "/nis2-gap-analyse",
  cta: "Zur Gap-Analyse",
};
const SCHULUNG: Empfehlung = {
  titel: "Schulung der Geschäftsführung",
  text: "Die Teilnahme der Leitungsorgane verlangt § 31 ausdrücklich. 3 Stunden, mit Teilnahmebestätigung.",
  href: "/nis2-schulung-geschaeftsfuehrung",
  cta: "Zur GF-Schulung",
};
const LIEFERANTEN: Empfehlung = {
  titel: "Lieferanten-Paket",
  text: "Den Fragebogen Ihres Kunden prüfbar beantworten – mit den Maßnahmen und Nachweisen, die dahinter stehen müssen.",
  href: "/nis2-lieferanten",
  cta: "Zum Lieferanten-Paket",
};
const GESPRAECH: Empfehlung = {
  titel: "Kostenloses Erstgespräch",
  text: "20 Minuten, in denen wir Ihre Einstufung gemeinsam durchgehen und den sinnvollsten nächsten Schritt festlegen.",
  href: "/kontakt?thema=nis2",
  cta: "Gespräch vereinbaren",
};

export function einstufen(a: Antworten): Ergebnis {
  const sektor = sektoren.find((s) => s.value === a.sektor);
  const gruppe: Sektorgruppe = sektor?.gruppe ?? "keiner";
  const g = groesse(a);
  const imSektor = gruppe === "anlage1" || gruppe === "anlage2";
  const kundeFragt = a.anforderung === "ja";
  const zusatzLieferant = kundeFragt
    ? ["Zusätzlich verlangt ein Kunde bereits Nachweise – das lässt sich in einem Zug mit erledigen."]
    : [];
  const sektorName = sektor?.label ?? "Ihr Bereich";

  if (gruppe === "groessenunabhaengig") {
    return {
      key: "groessenunabhaengig",
      titel: "Voraussichtlich betroffen – unabhängig von der Größe",
      kurz: `${sektorName} gehört zu den Bereichen, die das NISG 2026 unabhängig von der Unternehmensgröße erfasst.`,
      gruende: [
        "Für diese Dienste gelten die Größenschwellen nicht.",
        "Ob Sie als wesentliche oder wichtige Einrichtung gelten, hängt vom konkreten Dienst ab und gehört in die Einzelprüfung.",
        ...zusatzLieferant,
      ],
      pflichten: PFLICHTEN_DIREKT,
      empfehlung: GESPRAECH,
      zweitEmpfehlung: GAP,
      anlass: `Betroffenheitscheck: voraussichtlich größenunabhängig betroffen (${sektorName}).`,
    };
  }

  if (gruppe === "anlage1" && g === "gross") {
    return {
      key: "wesentlich",
      titel: "Voraussichtlich wesentliche Einrichtung",
      kurz: "Großes Unternehmen in einem Sektor mit hoher Kritikalität – die strengere der beiden Kategorien.",
      gruende: [
        `${sektorName} steht in Anlage 1 des NISG 2026.`,
        "Mit Ihrer Größe liegen Sie über den Schwellen für große Unternehmen.",
        ...zusatzLieferant,
      ],
      pflichten: PFLICHTEN_DIREKT,
      empfehlung: GAP,
      zweitEmpfehlung: SCHULUNG,
      anlass: `Betroffenheitscheck: voraussichtlich wesentliche Einrichtung (${sektorName}).`,
    };
  }

  if (imSektor && g !== "klein") {
    return {
      key: "wichtig",
      titel: "Voraussichtlich wichtige Einrichtung",
      kurz: "Mittleres oder großes Unternehmen in einem erfassten Sektor – Sie fallen voraussichtlich direkt unter das NISG 2026.",
      gruende: [
        `${sektorName} steht in Anlage ${gruppe === "anlage1" ? "1" : "2"} des NISG 2026.`,
        `Mit Ihrer Größe liegen Sie über der Schwelle für ${g === "gross" ? "große" : "mittlere"} Unternehmen.`,
        ...zusatzLieferant,
      ],
      pflichten: PFLICHTEN_DIREKT,
      empfehlung: GAP,
      zweitEmpfehlung: SCHULUNG,
      anlass: `Betroffenheitscheck: voraussichtlich wichtige Einrichtung (${sektorName}).`,
    };
  }

  if (imSektor && a.konzern === "ja") {
    return {
      key: "grenzfall",
      titel: "Grenzfall – die Konzernzahlen entscheiden",
      kurz: "Allein gerechnet liegen Sie unter den Schwellen. Weil Sie zu einer Gruppe gehören, können deren Zahlen aber mitzählen.",
      gruende: [
        `${sektorName} ist ein erfasster Sektor.`,
        "Bei Beteiligungen über 25 % werden Mitarbeitende, Umsatz und Bilanzsumme anteilig oder ganz zusammengerechnet – außer Ihre Netz- und Informationssysteme sind von denen der Gruppe unabhängig (§ 25 Abs. 4).",
        "Das kann Sie über die Schwelle heben – genau hier ist eine Einzelfallprüfung nötig.",
        ...zusatzLieferant,
      ],
      pflichten: [],
      empfehlung: {
        titel: "NIS2-Quick-Check",
        text: "Ein halber Tag: Einstufung mit Konzernzurechnung, die zehn größten Lücken, klare Empfehlung. Wo nötig mit Hinweis auf eine rechtliche Bestätigung.",
        href: "/nis2-beratung#pakete",
        cta: "Zum Quick-Check",
      },
      zweitEmpfehlung: GESPRAECH,
      anlass: "Betroffenheitscheck: Grenzfall durch Konzernzugehörigkeit – Einstufung klären.",
    };
  }

  if (kundeFragt || a.lieferant === "ja") {
    return {
      key: "indirekt",
      titel: "Indirekt betroffen – über Ihre Kunden",
      kurz: "Das Gesetz erfasst Sie voraussichtlich nicht selbst. Ihre Kunden müssen aber die Sicherheit ihrer Lieferkette nachweisen – und fragen deshalb bei Ihnen nach.",
      gruende: [
        imSektor
          ? "Ihr Sektor ist erfasst, Ihr Unternehmen liegt aber unter den Größenschwellen."
          : "Ihr Tätigkeitsbereich steht nicht in den Anlagen des NISG 2026.",
        kundeFragt
          ? "Ein Kunde verlangt bereits Nachweise – das ist in der Praxis der entscheidende Punkt, nicht die rechtliche Einstufung."
          : "Sie beliefern Branchen, die unter das Gesetz fallen. Fragebögen und Vertragsnachträge sind dort erfahrungsgemäß nur eine Frage der Zeit.",
      ],
      pflichten: [],
      empfehlung: LIEFERANTEN,
      zweitEmpfehlung: GESPRAECH,
      anlass: kundeFragt
        ? "Betroffenheitscheck: indirekt betroffen, ein Kunde verlangt bereits NIS2-Nachweise."
        : "Betroffenheitscheck: indirekt betroffen als Lieferant.",
    };
  }

  return {
    key: "nicht",
    titel: "Voraussichtlich nicht betroffen",
    kurz: "Nach Ihren Angaben fallen Sie weder direkt unter das NISG 2026 noch fragen Kunden bereits nach Nachweisen.",
    gruende: [
      imSektor
        ? "Ihr Sektor ist erfasst, Ihr Unternehmen liegt aber unter den Größenschwellen."
        : "Ihr Tätigkeitsbereich steht nicht in den Anlagen des NISG 2026.",
      a.lieferant === "unklar"
        ? "Offen ist, ob Ihre Kunden erfasst sind. Wenn ja, kommen deren Anforderungen früher oder später auch bei Ihnen an."
        : "Die zehn Maßnahmenbereiche sind trotzdem ein guter Maßstab für Grundschutz – und für die Fragen, die Cyberversicherungen stellen.",
    ],
    pflichten: [],
    empfehlung: {
      titel: "NIS2-Quick-Check als Grundschutz-Check",
      text: "Ein halber Tag: die zehn größten Lücken nach Risiko sortiert, ohne Verpflichtung zu mehr.",
      href: "/nis2-beratung#pakete",
      cta: "Zum Quick-Check",
    },
    anlass: "Betroffenheitscheck: voraussichtlich nicht betroffen – Interesse an Grundschutz.",
  };
}
